import { getCurrentOperatorName } from '../../utils/currentOperator';
import { calendarsFor } from '../../utils/businessDate';
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import { getTodayDate, calculateSettlementDate } from '../../utils/dateUtils';
import { provisionalSettleDate, resolveSettleDate } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import { useTenantConfig } from '../../contexts/TenantConfigContext';
import axios from 'axios';
import styled from 'styled-components';
import {
  ArrowRightLeft,
  CalendarDays,
  Globe2,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { DateField } from '../shared/DateField';

// FX-specific styled components
type FXTone = 'spot' | 'forward' | 'swap' | 'ndf';

const toneMap: Record<FXTone, {
  primary: string;
  secondary: string;
  soft: string;
  border: string;
  glow: string;
}> = {
  spot: {
    primary: '#38bdf8',
    secondary: '#7dd3fc',
    soft: 'rgba(56, 189, 248, 0.12)',
    border: 'rgba(56, 189, 248, 0.34)',
    glow: 'rgba(14, 165, 233, 0.18)',
  },
  forward: {
    primary: '#22c55e',
    secondary: '#86efac',
    soft: 'rgba(34, 197, 94, 0.12)',
    border: 'rgba(34, 197, 94, 0.34)',
    glow: 'rgba(34, 197, 94, 0.18)',
  },
  swap: {
    primary: '#a78bfa',
    secondary: '#c4b5fd',
    soft: 'rgba(167, 139, 250, 0.13)',
    border: 'rgba(167, 139, 250, 0.34)',
    glow: 'rgba(139, 92, 246, 0.2)',
  },
  ndf: {
    primary: '#f59e0b',
    secondary: '#fbbf24',
    soft: 'rgba(245, 158, 11, 0.12)',
    border: 'rgba(245, 158, 11, 0.34)',
    glow: 'rgba(217, 119, 6, 0.18)',
  },
};

const tone = (name: FXTone) => toneMap[name];

const FXWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FXHero = styled.section<{ $tone: FXTone }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 0.72fr);
  gap: 18px;
  align-items: stretch;
  padding: 18px;
  border-radius: 14px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background:
    radial-gradient(circle at 10% 0%, ${({ $tone }) => tone($tone).glow} 0%, rgba(15, 23, 42, 0) 35%),
    linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(2, 6, 23, 0.96) 100%);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.24);

  @media (max-width: 1040px) {
    grid-template-columns: 1fr;
  }
`;

const HeroMain = styled.div`
  display: flex;
  gap: 14px;
  min-width: 0;
`;

const HeroIcon = styled.div<{ $tone: FXTone }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $tone }) => tone($tone).secondary};
  background: ${({ $tone }) => tone($tone).soft};
  border: 1px solid ${({ $tone }) => tone($tone).border};
`;

const HeroEyebrow = styled.div<{ $tone: FXTone }>`
  color: ${({ $tone }) => tone($tone).secondary};
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const HeroTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: 22px;
  line-height: 1.18;
  letter-spacing: 0;
`;

const HeroDescription = styled.p`
  margin: 8px 0 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.5;
  max-width: 760px;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 13px;
`;

const ScopePill = styled.div<{ $tone: FXTone }>`
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${({ $tone }) => tone($tone).border};
  background: rgba(15, 23, 42, 0.58);
  color: #cbd5e1;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const HeroSignalGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const HeroSignal = styled.div<{ $tone: FXTone; $accent?: boolean }>`
  min-width: 0;
  padding: 13px;
  border-radius: 12px;
  border: 1px solid ${({ $tone, $accent }) => $accent ? tone($tone).border : 'rgba(71, 85, 105, 0.38)'};
  background: ${({ $tone, $accent }) => $accent ? tone($tone).soft : 'rgba(15, 23, 42, 0.68)'};
`;

const SignalLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const SignalValue = styled.div<{ $tone: FXTone; $accent?: boolean }>`
  color: ${({ $tone, $accent }) => $accent ? tone($tone).secondary : '#f8fafc'};
  font-size: 16px;
  font-weight: 900;
  line-height: 1.25;
  overflow-wrap: anywhere;
`;

const FXSection = styled.section`
  padding: 18px;
  border-radius: 14px;
  border: 1px solid rgba(51, 65, 85, 0.74);
  background: linear-gradient(180deg, rgba(15, 23, 42, 0.78) 0%, rgba(8, 13, 23, 0.9) 100%);
  min-width: 0;

  input,
  select {
    width: 100%;
    box-sizing: border-box;
  }
`;

const FXSectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 760px) {
    flex-direction: column;
  }
`;

const FXSectionEyebrow = styled.div<{ $tone: FXTone }>`
  color: ${({ $tone }) => tone($tone).secondary};
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const FXSectionTitle = styled.h3`
  margin: 0;
  color: #f8fafc;
  font-size: 17px;
  line-height: 1.24;
  letter-spacing: 0;
`;

const FXSectionSubline = styled.div`
  margin-top: 6px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.45;
`;

const SwapLegPanel = styled.div<{ isNear?: boolean }>`
  background: ${props => props.isNear
    ? 'radial-gradient(circle at 100% 0%, rgba(16, 185, 129, 0.12) 0%, rgba(15, 23, 42, 0) 42%), rgba(15, 23, 42, 0.72)'
    : 'radial-gradient(circle at 100% 0%, rgba(245, 158, 11, 0.13) 0%, rgba(15, 23, 42, 0) 42%), rgba(15, 23, 42, 0.72)'};
  border: 1px solid ${props => props.isNear ? 'rgba(16, 185, 129, 0.34)' : 'rgba(245, 158, 11, 0.34)'};
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.18);

  input,
  select {
    width: 100%;
    box-sizing: border-box;
  }
`;

const SwapLegTitle = styled.h3<{ isNear?: boolean }>`
  font-size: 14px;
  font-weight: 850;
  color: ${props => props.isNear ? '#34d399' : '#fbbf24'};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const RateSummary = styled.div`
  background:
    radial-gradient(circle at 100% 0%, rgba(14, 165, 233, 0.16) 0%, rgba(15, 23, 42, 0) 42%),
    rgba(15, 23, 42, 0.74);
  border-radius: 14px;
  padding: 18px;
  border: 1px solid rgba(56, 189, 248, 0.28);
`;

const RateSummaryTitle = styled.h3`
  font-size: 16px;
  font-weight: 900;
  color: #e2e8f0;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 9px;
`;

const RateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
`;

const RateCard = styled.div`
  min-width: 0;
  background: rgba(2, 6, 23, 0.34);
  padding: 12px 13px;
  border-radius: 11px;
  border: 1px solid rgba(71, 85, 105, 0.36);
`;

const RateLabel = styled.div`
  color: #64748b;
  font-size: 10px;
  font-weight: 850;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const RateValue = styled.div<{ highlight?: boolean }>`
  font-size: 16px;
  font-weight: 900;
  line-height: 1.24;
  color: ${props => props.highlight ? '#7dd3fc' : '#f8fafc'};
  overflow-wrap: anywhere;
`;

const CurrencyPairDisplay = styled.div<{ $tone: FXTone }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 26px;
  font-weight: 900;
  color: #f1f5f9;
  padding: 18px;
  background:
    linear-gradient(135deg, ${({ $tone }) => tone($tone).soft} 0%, rgba(15, 23, 42, 0.72) 100%);
  border: 1px solid ${({ $tone }) => tone($tone).border};
  border-radius: 14px;
`;

const SwapArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  color: #7dd3fc;
  background: rgba(2, 6, 23, 0.64);
  border: 1px solid rgba(125, 211, 252, 0.32);
`;

type FXProductType = 'SPOT' | 'FORWARD' | 'FX_SWAP' | 'NDF';

const FX_PRODUCT_CONFIG: Record<FXProductType, {
  toneName: FXTone;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  description: string;
  badges: string[];
}> = {
  SPOT: {
    toneName: 'spot',
    icon: Globe2,
    eyebrow: 'FX Spot',
    title: 'Spot FX Ticket',
    description: 'Near-date currency exchange with live pair context, counter amount, rate, and value date.',
    badges: ['T+2 value date', 'Currency pair', 'Counterparty'],
  },
  FORWARD: {
    toneName: 'forward',
    icon: CalendarDays,
    eyebrow: 'FX Forward',
    title: 'Forward FX Ticket',
    description: 'Dated currency exchange with forward points, outright rate, and settlement discipline.',
    badges: ['Forward points', 'Outright rate', 'Value date'],
  },
  FX_SWAP: {
    toneName: 'swap',
    icon: RefreshCw,
    eyebrow: 'FX Swap',
    title: 'FX Swap Ticket',
    description: 'Near and far leg currency exchange with swap points and reverse settlement economics.',
    badges: ['Near leg', 'Far leg', 'Swap points'],
  },
  NDF: {
    toneName: 'ndf',
    icon: ShieldCheck,
    eyebrow: 'Non-Deliverable Forward',
    title: 'NDF Ticket',
    description: 'Cash-settled FX forward with fixing date, source, forward points, and settlement terms.',
    badges: ['Fixing source', 'Cash settlement', 'Forward points'],
  },
};

const formatAmountSignal = (value: string, currency: string) => {
  const parsed = value ? parseAmountShorthand(value) : 0;
  if (!Number.isFinite(parsed) || parsed <= 0) return '-';
  return `${parsed.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${currency || ''}`.trim();
};

// F-FX-015 (FX E2E 2026-08-08): rates are quoted QUOTE-per-BASE by market
// convention (EURUSD 1.1000 = 1 EUR buys 1.1000 USD), and which currency is
// the base is a market fact, not a function of which side the operator typed
// into "Buy". The pre-fix screen computed sellAmount = buyAmount / rate,
// making the rate "buy-ccy per sell-ccy" — so buying EUR against USD required
// entering the inverted 0.9091 instead of 1.1000.
const FX_BASE_PRECEDENCE = ['EUR', 'GBP', 'AUD', 'NZD', 'USD'];

export const deriveFxPair = (ccyA: string, ccyB: string): { base: string; quote: string; pair: string } => {
  const rank = (c: string) => {
    const i = FX_BASE_PRECEDENCE.indexOf((c || '').toUpperCase());
    return i === -1 ? 99 : i;
  };
  const a = (ccyA || '').toUpperCase();
  const b = (ccyB || '').toUpperCase();
  const base = rank(a) <= rank(b) ? a : b;
  const quote = base === a ? b : a;
  return { base, quote, pair: `${base}${quote}` };
};

// Pip factor: 4-decimal pairs move in 0.0001; JPY and most EM pairs quote to
// 2 decimals and move in 0.01. Forward/swap points must use the right one.
/** Both currencies' calendars date an FX trade, with USD. */
const fxCalendars = (buy?: string | null, sell?: string | null) => calendarsFor('FX', buy || 'USD', sell || 'EUR');
/**
 * Trade date calendars, as the server checks them: an FX trade is booked in its USD side, so a
 * USD pair trades on any USD business day; a cross is checked on both of its currencies.
 */
const fxTradeCalendars = (buy?: string | null, sell?: string | null) =>
  ((buy || 'USD') === 'USD' || (sell || 'EUR') === 'USD') ? calendarsFor('FX', 'USD') : fxCalendars(buy, sell);

export const fxPipFactor = (quoteCurrency: string): number =>
  ['JPY', 'BRL', 'KRW', 'CLP', 'COP', 'HUF', 'IDR'].includes((quoteCurrency || '').toUpperCase())
    ? 100
    : 10000;

/** Spot value date: T+2 on both currencies' calendars and USD. The server has the last word. */
const valueDateQuery = (tradeDate: string, buy?: string | null, sell?: string | null) =>
  ({ tradeDate, ticketType: 'FX', convention: 'T+2', currency: buy || 'USD', counterCurrency: sell || 'EUR' });

/** The provisional value date at once, then the server's, unless the form has moved on meanwhile. */
const syncValueDate = (setFormData: React.Dispatch<React.SetStateAction<any>>, tradeDate: string,
  buy: string, sell: string) => {
  const query = valueDateQuery(tradeDate, buy, sell);
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  void resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === tradeDate && prev.buyCurrency === buy && prev.sellCurrency === sell
      && prev.settleDate === provisional ? { ...prev, settleDate } : prev));
};

/** Runs once when a new ticket opens. */
const OnOpen: React.FC<{ run: () => void }> = ({ run }) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, []);
  return null;
};

/**
 * Read the FX silo row a booked trade points at. `dim_fx_trade` is the system
 * of record for FX economics — the trade itself carries only qty/price/net —
 * so reopening a ticket has to come back through here (F-FX-019).
 */
const fetchFxSilo = async (sourceRef?: string): Promise<any | null> => {
  const match = /^fx-trade:(\d+):/.exec(sourceRef || '');
  if (!match) return null;
  try {
    const { data } = await axios.get(`/api/derivatives/fx/trade/${match[1]}`);
    return data || null;
  } catch (err) {
    console.warn('Failed to load FX silo row for', sourceRef, err);
    return null;
  }
};

const formatRateSignal = (value: string) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 })
    : '-';
};

const FXTicketScreen: React.FC = () => {
  const location = useLocation();
  const { isRetail } = useTenantConfig();
  
  // Determine initial product type from route
  const getProductTypeFromPath = (path: string): FXProductType => {
    const lowerPath = path.toLowerCase();
    if (lowerPath.includes('/ndf')) return 'NDF';
    if (lowerPath.includes('/swap')) return 'FX_SWAP';
    if (lowerPath.includes('/forward')) return 'FORWARD';
    return 'SPOT';
  };
  
  const [fxProductType, setFXProductType] = useState<FXProductType>(() => getProductTypeFromPath(location.pathname));
  const [marketRate, setMarketRate] = useState<number | null>(null);
  const [loadingRate, setLoadingRate] = useState(false);
  
  // Update product type when route changes
  useEffect(() => {
    const newType = getProductTypeFromPath(location.pathname);
    setFXProductType(newType);
  }, [location.pathname]);

  const initialProductType = getProductTypeFromPath(location.pathname);
  
  const getSubClassFromType = (type: FXProductType): string => {
    switch (type) {
      case 'SPOT': return 'Spot';
      case 'FORWARD': return 'Forward';
      case 'FX_SWAP': return 'FX Swap';
      case 'NDF': return 'NDF';
      default: return 'Spot';
    }
  };

  // Check for pending trade data from Discovery or other sources
  const getPendingTradeData = () => {
    try {
      const pendingData = sessionStorage.getItem('pendingTradeData');
      if (pendingData) {
        const parsed = JSON.parse(pendingData);
        // Clear the sessionStorage after reading
        sessionStorage.removeItem('pendingTradeData');
        return parsed;
      }
    } catch (error) {
      console.error('Error reading pending trade data:', error);
    }
    return null;
  };

  const pendingTrade = getPendingTradeData();
  // A trade opened from the blotter keeps its stored value date.
  const openingTrade = !!((location.state as any)?.transactionId || (location.state as any)?.id);
  const openTradeDate = pendingTrade?.tradeDate
    || getTodayDate(fxTradeCalendars(pendingTrade?.buyCurrency, pendingTrade?.sellCurrency));

  const initialFormData = {
    transactionType: (pendingTrade?.transactionType || 'BUY') as 'BUY' | 'SELL',
    fxProductType: pendingTrade?.fxProductType || initialProductType,
    assetManager: pendingTrade?.assetManager || '',
    portfolioManager: pendingTrade?.portfolioManager || '',
    assetClass: 'FX',
    assetSubClass: pendingTrade?.assetSubClassName || getSubClassFromType(initialProductType),
    portfolio: pendingTrade?.portfolioName || pendingTrade?.portfolio || '',
    // Currency pair
    buyCurrency: pendingTrade?.buyCurrency || 'USD',
    sellCurrency: pendingTrade?.sellCurrency || 'EUR',
    buyAmount: pendingTrade?.buyAmount?.toString() || pendingTrade?.amount?.toString() || '',
    sellAmount: pendingTrade?.sellAmount?.toString() || '',
    // Rate
    rate: pendingTrade?.rate?.toString() || '',
    // Dates
    tradeDate: openTradeDate,
    settleDate: pendingTrade?.settleDate || provisionalSettleDate(
      valueDateQuery(openTradeDate, pendingTrade?.buyCurrency, pendingTrade?.sellCurrency)),
    // For forwards/swaps
    forwardPoints: pendingTrade?.forwardPoints?.toString() || '',
    forwardRate: pendingTrade?.forwardRate?.toString() || '',
    // For FX Swap - far leg
    farSettleDate: pendingTrade?.farSettleDate || '',
    farRate: pendingTrade?.farRate?.toString() || '',
    farBuyAmount: pendingTrade?.farBuyAmount?.toString() || '',
    farSellAmount: pendingTrade?.farSellAmount?.toString() || '',
    // For NDF
    fixingDate: pendingTrade?.fixingDate || '',
    fixingSource: pendingTrade?.fixingSource || 'WM/Reuters',
    // Common
    submittedBy: pendingTrade?.submittedBy || getCurrentOperatorName(),
    executedBy: pendingTrade?.executedBy || '',
    notes: pendingTrade?.notes || '',
    rationale: pendingTrade?.rationale || '',
    counterparty: pendingTrade?.counterparty || '',
    // Execution details
    executionBroker: pendingTrade?.executionBroker || '',
    executionVenue: pendingTrade?.executionVenue || '',
    executionId: pendingTrade?.executionId || '',
    executionTime: pendingTrade?.executionTime || ''
  };

  // Fetch market rate for the pair the operator is actually being asked to
  // quote. F-FX-018: this used to pass (buyCcy, sellCcy) straight through as
  // base/quote, so on a USD-buy ticket it fetched USD→EUR (0.9091) while the
  // Exchange Rate label demanded the market-convention EURUSD reading
  // (1.1000) — an operator trusting the prefill booked an inverted rate. The
  // fetch must use the SAME deriveFxPair orientation as the label.
  const fetchMarketRate = useCallback(async (ccyA: string, ccyB: string) => {
    if (!ccyA || !ccyB || ccyA === ccyB) {
      setMarketRate(null);
      return;
    }

    const { base, quote } = deriveFxPair(ccyA, ccyB);
    setLoadingRate(true);
    try {
      const response = await axios.get(`/api/market-data/fx-rate`, {
        params: { baseCurrency: base, quoteCurrency: quote }
      });
      if (response.data && response.data.rate) {
        setMarketRate(Number(response.data.rate));
      } else {
        setMarketRate(null);
      }
    } catch (error) {
      console.warn('Failed to fetch FX rate:', error);
      setMarketRate(null);
    } finally {
      setLoadingRate(false);
    }
  }, []);

  // F-FX-017: the rate was only ever fetched from the two currency onChange
  // handlers, so a ticket opened on its default pair showed no market rate at
  // all — the /api/market-data/fx-rate wiring was unreachable until the
  // operator touched a currency dropdown. Prime it on mount.
  useEffect(() => {
    const buyCcy = pendingTrade?.buyCurrency || 'USD';
    const sellCcy = pendingTrade?.sellCurrency || 'EUR';
    fetchMarketRate(buyCcy, sellCcy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateForm = (formData: any, action: string, useAllocations: boolean = false): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    const productType = formData.fxProductType as FXProductType;

    // Common validations - Asset Manager only for institutional
    if (!isRetail && !formData.assetManager?.trim()) errors.assetManager = 'Asset Manager is required';
    if (!formData.portfolioManager?.trim()) errors.portfolioManager = isRetail ? 'Advisor is required' : 'Portfolio Manager is required';
    if (!useAllocations && !formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settlement Date is required';
    
    // Currency validations
    if (!formData.buyCurrency) errors.buyCurrency = 'Buy Currency is required';
    if (!formData.sellCurrency) errors.sellCurrency = 'Sell Currency is required';
    if (formData.buyCurrency === formData.sellCurrency) {
      errors.sellCurrency = 'Buy and Sell currencies must be different';
    }
    
    // Amount validations
    if (!formData.buyAmount && !formData.sellAmount) {
      errors.buyAmount = 'Either Buy or Sell amount is required';
    }
    
    // Rate validation
    if (!formData.rate || parseFloat(formData.rate) <= 0) {
      errors.rate = 'Valid exchange rate is required';
    }
    
    // Product-specific validations
    if (productType === 'FORWARD' || productType === 'NDF') {
      // Forward date must be after spot date
      const spotDate = new Date(calculateSettlementDate(formData.tradeDate, 'T+2',
        fxCalendars(formData.buyCurrency, formData.sellCurrency)));
      const settleDate = new Date(formData.settleDate);
      if (settleDate <= spotDate) {
        errors.settleDate = 'Forward settlement must be after spot date (T+2)';
      }
    }
    
    if (productType === 'FX_SWAP') {
      if (!formData.farSettleDate) errors.farSettleDate = 'Far leg settlement date is required';
      if (!formData.farRate) errors.farRate = 'Far leg rate is required';
    }
    
    if (productType === 'NDF') {
      if (!formData.fixingDate) errors.fixingDate = 'Fixing date is required';
    }
    
    // Execution validations
    if (action === 'execute') {
      if (!formData.executedBy?.trim()) {
        errors.executedBy = 'Executed By is required for execution';
      }
    }

    return errors;
  };

  const buildTransactionData = async (formData: any, dimensionData: any, useAllocations: boolean = false): Promise<any> => {
    // Validate required fields
    if (!formData.buyCurrency || !formData.sellCurrency) {
      throw new Error('Currency pair is required');
    }
    
    const buyAmount = formData.buyAmount ? parseAmountShorthand(formData.buyAmount) : 0;
    const sellAmount = formData.sellAmount ? parseAmountShorthand(formData.sellAmount) : 0;
    
    if (buyAmount <= 0 && sellAmount <= 0) {
      throw new Error('Either buy or sell amount is required');
    }

    // Look up portfolio
    let portfolioId: number | null = null;
    if (!useAllocations) {
      if (!formData.portfolio?.trim()) {
        throw new Error('Portfolio is required');
      }
      const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
      if (!portfolio) {
        throw new Error(`Portfolio "${formData.portfolio}" not found`);
      }
      portfolioId = portfolio.id;
    }

    // Look up portfolio manager ID
    let portfolioManagerId: number | null = null;
    if (formData.portfolioManager && formData.portfolioManager.trim()) {
      const pm = dimensionData.portfolioManagers.find((pm: any) => pm.portfolioManagerName === formData.portfolioManager.trim());
      if (pm && pm.id) {
        portfolioManagerId = pm.id;
      }
    }

    // FX E2E rewire (2026-08-08, F-FX-001/002): the ticket's terms ARE the
    // contract. /prepare mints the dim_fx_trade silo row and the SMF (an
    // FX-prefix classified security for FORWARD/SWAP/NDF; the {BASE}_CASH
    // security for SPOT, which is a currency exchange, not a derivative) and
    // returns the sourceRef the FX posting SPI resolves at POST. The old path
    // POSTed 18 FX fields to the generic endpoint, where every one of them was
    // silently discarded and no securityId was ever set.
    const productType = formData.fxProductType as FXProductType;
    const fxType = productType === 'FX_SWAP' ? 'SWAP' : productType;
    const { base, quote } = deriveFxPair(formData.buyCurrency, formData.sellCurrency);
    // The Direction selector ("Buy USD / Sell EUR" vs "Sell USD / Buy EUR")
    // flips which currency is actually bought; the amount fields stay tied to
    // their currency fields either way.
    const boughtCurrency = formData.transactionType === 'SELL'
      ? formData.sellCurrency : formData.buyCurrency;
    // Direction is stated against the BASE currency (market convention);
    // baseAmount is whichever amount field carries the base currency.
    const buySellBase = (boughtCurrency || '').toUpperCase() === base ? 'BUY' : 'SELL';
    const baseAmount = (formData.buyCurrency || '').toUpperCase() === base ? buyAmount : sellAmount;
    const spotRate = parseFloat(formData.rate) || 0;
    const outrightRate = formData.forwardRate ? parseFloat(formData.forwardRate) : spotRate;

    const preparePayload: any = {
      portfolioId,
      fxType,
      buySellBase,
      baseCurrency: base,
      quoteCurrency: quote,
      baseAmount,
      tradeDate: formData.tradeDate,
      counterpartyName: formData.counterparty || null,
    };

    if (fxType === 'SPOT') {
      preparePayload.rate = spotRate;
      preparePayload.valueDate = formData.settleDate;
    } else if (fxType === 'FORWARD') {
      preparePayload.rate = outrightRate;
      preparePayload.forwardDate = formData.settleDate;
    } else if (fxType === 'SWAP') {
      preparePayload.nearLegRate = spotRate;
      preparePayload.nearLegDate = formData.settleDate;
      preparePayload.farLegRate = parseFloat(formData.farRate) || 0;
      preparePayload.farLegDate = formData.farSettleDate;
    } else if (fxType === 'NDF') {
      preparePayload.rate = outrightRate;
      preparePayload.fixingDate = formData.fixingDate;
      preparePayload.fixingSource = formData.fixingSource || 'WM/Reuters';
      preparePayload.forwardDate = formData.settleDate;
    }

    const prepareRes = await axios.post('/api/derivatives/fx/prepare', preparePayload);
    const { securityId, sourceRef, inceptionCash } = prepareRes.data;

    return {
      transactionType: buySellBase,
      securityId,
      portfolioId,
      portfolioManagerId,
      // quantity = base-currency notional; price = the contract rate.
      // netAmount is SERVER-computed: the signed quote-currency cash for a
      // SPOT exchange, 0 for the zero-cash derivative types. The FX ticket
      // types are in both netAmount guards, so this value survives.
      quantity: baseAmount,
      price: fxType === 'SWAP' ? spotRate : (fxType === 'SPOT' ? spotRate : outrightRate),
      amount: baseAmount,
      netAmount: inceptionCash ?? 0,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes
        || `FX ${fxType} ${buySellBase} ${base}${quote} ${baseAmount.toLocaleString('en-US')} @ ${fxType === 'SWAP' ? spotRate : outrightRate}`,
      rationale: formData.rationale || '',
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      counterparty: formData.counterparty || null,
      status: 'DRAFT',
      ticketType: 'FX',
      eventType: 'ACQUIRE',
      sourceRef,
    };
  };

  /**
   * F-FX-019: a drafted FX ticket reopened from the blotter came back blank —
   * currencies reset to the USD/EUR defaults, amounts and rate empty —
   * because every FX field below was read off the trade, and none of them are
   * columns on `fact_trade_transactions`. The FX economics live in
   * `dim_fx_trade`; the ticket has to read them back from the silo using the
   * `source_ref` the trade already carries.
   *
   * Worse than blank: the screen rendered the DEFAULT pair (USD/EUR, "Buy USD
   * / Sell EUR") over a trade actually booked BUY EUR on EURUSD, so it
   * misrepresented the position — and a re-save would have written that
   * fiction over the real economics.
   *
   * Async because BaseTicketScreen awaits this before populating the form,
   * the same extension point the portco-debt ticket uses to read its silo.
   */
  const loadTransactionData = async (transaction: any) => {
    const silo = await fetchFxSilo(transaction.sourceRef);

    const num = (v: any) => (v === null || v === undefined ? null : Number(v));
    const amt = (v: any) => {
      const n = num(v);
      return n === null ? null : formatAmountWithCommas(n);
    };
    const siloProductType: FXProductType | null = silo
      ? (silo.fxType === 'SWAP' ? 'FX_SWAP' : ((silo.fxType as FXProductType) || null))
      : null;
    if (siloProductType) {
      setFXProductType(siloProductType);
    }

    // F-FX-021: the silo states direction against the BASE currency
    // (buySellBase), but the form's Buy/Sell fields mean "the currency I
    // bought / sold". Mapping base→Buy unconditionally produced a ticket that
    // contradicted itself on a base-sell: Direction read "Sell EUR / Buy USD"
    // while the amount beside it was labelled "Buy Amount (EUR)". Resolve the
    // silo's base-relative direction into bought/sold before filling the
    // fields, so the labels are true and the form reads back the way it was
    // entered. Direction is then always "Buy <bought> / Sell <sold>";
    // buildTransactionData re-derives buySellBase from base precedence, so
    // this round-trips to the same silo values.
    const soldBase = silo?.buySellBase === 'SELL';
    const boughtCurrency = silo ? (soldBase ? silo.quoteCurrency : silo.baseCurrency) : null;
    const soldCurrency = silo ? (soldBase ? silo.baseCurrency : silo.quoteCurrency) : null;
    const boughtAmount = silo ? (soldBase ? silo.quoteAmount : silo.baseAmount) : null;
    const soldAmount = silo ? (soldBase ? silo.baseAmount : silo.quoteAmount) : null;

    return {
      transactionType: silo ? 'BUY' : (transaction.transactionType || 'BUY'),
      fxProductType: siloProductType || transaction.fxProductType || transaction.assetSubClass || 'SPOT',
      assetManager: transaction.assetManagerName || '',
      portfolioManager: transaction.portfolioManagerName || '',
      assetClass: 'FX',
      assetSubClass: transaction.assetSubClass || 'Spot',
      portfolio: transaction.portfolioName || '',
      buyCurrency: boughtCurrency || transaction.buyCurrency || 'USD',
      sellCurrency: soldCurrency || transaction.sellCurrency || 'EUR',
      buyAmount: amt(boughtAmount) ?? (transaction.buyAmount?.toString() || ''),
      sellAmount: amt(soldAmount) ?? (transaction.sellAmount?.toString() || ''),
      rate: num(silo?.spotRate) !== null
        ? String(num(silo?.spotRate))
        : (transaction.rate?.toString() || transaction.price?.toString() || ''),
      tradeDate: silo?.tradeDate || transaction.tradeDate
        || getTodayDate(fxTradeCalendars(boughtCurrency || transaction.buyCurrency, soldCurrency || transaction.sellCurrency)),
      settleDate: silo?.valueDate || transaction.settleDate || calculateSettlementDate(
        getTodayDate(fxTradeCalendars(boughtCurrency || transaction.buyCurrency, soldCurrency || transaction.sellCurrency)), 'T+2',
        fxCalendars(boughtCurrency || transaction.buyCurrency, soldCurrency || transaction.sellCurrency)),
      forwardPoints: num(silo?.forwardPoints) !== null
        ? String(num(silo?.forwardPoints)) : (transaction.forwardPoints?.toString() || ''),
      forwardRate: num(silo?.forwardRate) !== null
        ? String(num(silo?.forwardRate)) : (transaction.forwardRate?.toString() || ''),
      farSettleDate: silo?.farLegDate || transaction.farSettleDate || '',
      farRate: num(silo?.farLegRate) !== null
        ? String(num(silo?.farLegRate)) : (transaction.farRate?.toString() || ''),
      farBuyAmount: transaction.farBuyAmount?.toString() || '',
      farSellAmount: transaction.farSellAmount?.toString() || '',
      fixingDate: silo?.fixingDate || transaction.fixingDate || '',
      fixingSource: silo?.fixingSource || transaction.fixingSource || 'WM/Reuters',
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      rationale: transaction.rationale || '',
      counterparty: silo?.counterpartyName || transaction.counterparty || '',
      executionBroker: transaction.executionBroker || '',
      executionVenue: transaction.executionVenue || '',
      executionId: transaction.executionId || '',
      executionTime: transaction.executionTime || '',
      sourceRef: transaction.sourceRef || ''
    };
  };

  // Calculate counter amount when one amount and rate change.
  // F-FX-015: the rate is ALWAYS quote-per-base for the market-convention
  // pair, so the multiply/divide depends on which side of the pair the
  // operator's Buy currency sits on — not on the field they typed into.
  const calculateCounterAmount = (
    buyAmount: string,
    sellAmount: string,
    rate: string,
    changedField: 'buy' | 'sell' | 'rate',
    setFormData: any,
    buyCurrency: string,
    sellCurrency: string
  ) => {
    const rateNum = parseFloat(rate) || 0;
    if (rateNum <= 0) return;

    const { base } = deriveFxPair(buyCurrency, sellCurrency);
    const buyingBase = (buyCurrency || '').toUpperCase() === base;

    if (changedField === 'buy' || changedField === 'rate') {
      const buyNum = parseAmountShorthand(buyAmount);
      if (buyNum > 0) {
        // Buying base → the sell side is quote = base × rate.
        // Buying quote → the sell side is base = quote / rate.
        const sellNum = buyingBase ? buyNum * rateNum : buyNum / rateNum;
        setFormData((prev: any) => ({
          ...prev,
          sellAmount: formatAmountWithCommas(Number(sellNum.toFixed(2)))
        }));
      }
    } else if (changedField === 'sell') {
      const sellNum = parseAmountShorthand(sellAmount);
      if (sellNum > 0) {
        const buyNum = buyingBase ? sellNum / rateNum : sellNum * rateNum;
        setFormData((prev: any) => ({
          ...prev,
          buyAmount: formatAmountWithCommas(Number(buyNum.toFixed(2)))
        }));
      }
    }
  };

  // Get the appropriate title based on product type
  const getTitle = () => {
    switch (fxProductType) {
      case 'SPOT': return 'FX Spot Trade Entry';
      case 'FORWARD': return 'FX Forward Trade Entry';
      case 'FX_SWAP': return 'FX Swap Trade Entry';
      case 'NDF': return 'NDF Trade Entry';
      default: return 'FX Trade Entry';
    }
  };

  return (
    <BaseTicketScreen
      key={location.pathname} // Force remount when path changes
      title={getTitle()}
      assetClass="FX"
      hidePortfolioField={true}
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, handleInputBlur, useAllocations, households, selectedHousehold, setSelectedHousehold, filteredPortfolios }) => {
        const productConfig = FX_PRODUCT_CONFIG[fxProductType];
        const ProductIcon = productConfig.icon;
        const toneName = productConfig.toneName;
        const valueDateLabel = fxProductType === 'FX_SWAP' ? 'Near Value Date' : fxProductType === 'NDF' ? 'Fixing / Settle' : 'Value Date';
        const productSignal = fxProductType === 'FX_SWAP'
          ? (formData.farSettleDate || '-')
          : fxProductType === 'NDF'
            ? (formData.fixingDate || '-')
            : (formData.settleDate || '-');

        return (
        <FXWorkspace>
          {!openingTrade && <OnOpen run={() => {
            // A new ticket asks the server for the spot value date; a value date handed in stays.
            if (!pendingTrade?.settleDate) syncValueDate(setFormData, formData.tradeDate, formData.buyCurrency, formData.sellCurrency);
          }} />}
          <FXHero $tone={toneName}>
            <HeroMain>
              <HeroIcon $tone={toneName}><ProductIcon size={22} /></HeroIcon>
              <div>
                <HeroEyebrow $tone={toneName}>{productConfig.eyebrow}</HeroEyebrow>
                <HeroTitle>{productConfig.title}</HeroTitle>
                <HeroDescription>{productConfig.description}</HeroDescription>
                <PillRow>
                  {productConfig.badges.map((badge) => (
                    <ScopePill key={badge} $tone={toneName}>{badge}</ScopePill>
                  ))}
                </PillRow>
              </div>
            </HeroMain>
            <HeroSignalGrid>
              <HeroSignal $tone={toneName} $accent>
                <SignalLabel>Pair</SignalLabel>
                <SignalValue $tone={toneName} $accent>{formData.buyCurrency}/{formData.sellCurrency}</SignalValue>
              </HeroSignal>
              <HeroSignal $tone={toneName} $accent>
                <SignalLabel>Buy Amount</SignalLabel>
                <SignalValue $tone={toneName} $accent>{formatAmountSignal(formData.buyAmount, formData.buyCurrency)}</SignalValue>
              </HeroSignal>
              <HeroSignal $tone={toneName}>
                <SignalLabel>Rate</SignalLabel>
                <SignalValue $tone={toneName}>{formatRateSignal(formData.rate)}</SignalValue>
              </HeroSignal>
              <HeroSignal $tone={toneName}>
                <SignalLabel>{valueDateLabel}</SignalLabel>
                <SignalValue $tone={toneName}>{productSignal}</SignalValue>
              </HeroSignal>
            </HeroSignalGrid>
          </FXHero>

          <CurrencyPairDisplay $tone={toneName}>
            <span>{formData.buyCurrency}</span>
            <SwapArrow><ArrowRightLeft size={18} /></SwapArrow>
            <span>{formData.sellCurrency}</span>
          </CurrencyPairDisplay>

          <FXSection>
            <FXSectionHeader>
              <div>
                <FXSectionEyebrow $tone={toneName}>Trade Setup</FXSectionEyebrow>
                <FXSectionTitle>Dates and Ownership</FXSectionTitle>
                <FXSectionSubline>Capture value date, manager, and trader context for the FX ticket. Counterparty is selected in Execution &amp; Clearing.</FXSectionSubline>
              </div>
            </FXSectionHeader>

            {/* Row 1: Trade Date, Settlement Date */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="tradeDate">Trade Date*</Label>
              <DateField
                id="tradeDate"
                value={formData.tradeDate ?? ''}
                className={errors.tradeDate ? 'error' : ''}
                disabled={isFieldDisabled('tradeDate')}
                calendars={fxTradeCalendars(formData.buyCurrency, formData.sellCurrency)}
                onChange={iso => {
                handleInputChange('tradeDate', iso);
                // Update settle date based on product type
                if (fxProductType === 'SPOT') {
                syncValueDate(setFormData, iso, formData.buyCurrency, formData.sellCurrency);
                }
                }}
              />
              {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="settleDate">
                {fxProductType === 'FX_SWAP' ? 'Near Leg Settle*' : 'Settlement Date*'}
              </Label>
              <DateField
                id="settleDate"
                value={formData.settleDate ?? ''}
                className={errors.settleDate ? 'error' : ''}
                disabled={isFieldDisabled('settleDate')}
                calendars={fxCalendars(formData.buyCurrency, formData.sellCurrency)}
                onChange={iso => handleInputChange('settleDate', iso)}
              />
              {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
            </FormGroup>

            {/* F-FX-024: counterparty moved to Execution & Clearing, where it
                is a reference-data Select like every other OTC derivative
                lane. Free text here let "GS" and "Goldman Sachs & Co. LLC"
                land in dim_fx_trade.counterparty_name as different names. */}
          </FormRow>

          {/* Row 2: Asset Manager (institutional only), Portfolio Manager/Advisor, Executed By */}
          <FormRow>
            {!isRetail && (
              <FormGroup>
                <Label htmlFor="assetManager">Asset Manager*</Label>
                <Select
                  id="assetManager"
                  value={formData.assetManager}
                  onChange={(e) => handleInputChange('assetManager', e.target.value)}
                  className={errors.assetManager ? 'error' : ''}
                  disabled={isFieldDisabled('assetManager')}
                >
                  <option value="">Select Asset Manager</option>
                  {dimensionData.assetManagers.map((am: any) => (
                    <option key={am.id} value={am.assetManagerName}>
                      {am.assetManagerName}
                    </option>
                  ))}
                </Select>
                {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
              </FormGroup>
            )}

            <FormGroup>
              <Label htmlFor="portfolioManager">{isRetail ? 'Advisor*' : 'Portfolio Manager*'}</Label>
              <Select
                id="portfolioManager"
                value={formData.portfolioManager}
                onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                className={errors.portfolioManager ? 'error' : ''}
                disabled={isFieldDisabled('portfolioManager')}
              >
                <option value="">Select Portfolio Manager</option>
                {dimensionData.portfolioManagers.map((pm: any) => (
                  <option key={pm.id} value={pm.portfolioManagerName}>
                    {pm.portfolioManagerName}
                  </option>
                ))}
              </Select>
              {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
              <Select
                id="executedBy"
                value={formData.executedBy}
                onChange={(e) => handleInputChange('executedBy', e.target.value)}
                disabled={transactionStatus === 'EXECUTED' || transactionStatus === 'CANCELLED'}
                className={errors.executedBy ? 'error' : ''}
              >
                <option value="">Select</option>
                {dimensionData.users?.map((user: any) => (
                  <option key={user.id} value={user.fullName}>
                    {user.fullName} {user.role ? `(${user.role})` : ''}
                  </option>
                ))}
              </Select>
              {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
            </FormGroup>
          </FormRow>

          </FXSection>

          <FXSection>
            <FXSectionHeader>
              <div>
                <FXSectionEyebrow $tone={toneName}>Currency Exchange</FXSectionEyebrow>
                <FXSectionTitle>Pair, Direction, Amounts, and Rate</FXSectionTitle>
                <FXSectionSubline>Enter the bought and sold currencies, then the rate-driven counter amount.</FXSectionSubline>
              </div>
            </FXSectionHeader>

            {/* Row 3: Buy Currency, Sell Currency, Transaction Type */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="buyCurrency">Buy Currency*</Label>
              <Select
                id="buyCurrency"
                value={formData.buyCurrency}
                onChange={(e) => {
                  handleInputChange('buyCurrency', e.target.value);
                  fetchMarketRate(e.target.value, formData.sellCurrency);
                  // The spot value date moves with the currencies' calendars.
                  if (fxProductType === 'SPOT') syncValueDate(setFormData, formData.tradeDate, e.target.value, formData.sellCurrency);
                }}
                className={errors.buyCurrency ? 'error' : ''}
                disabled={isFieldDisabled('buyCurrency')}
              >
                <option value="">Select Currency</option>
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>
                    {c.currencyCode} - {c.currencyName}
                  </option>
                ))}
              </Select>
              {errors.buyCurrency && <FieldError>{errors.buyCurrency}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="sellCurrency">Sell Currency*</Label>
              <Select
                id="sellCurrency"
                value={formData.sellCurrency}
                onChange={(e) => {
                  handleInputChange('sellCurrency', e.target.value);
                  fetchMarketRate(formData.buyCurrency, e.target.value);
                  // The spot value date moves with the currencies' calendars.
                  if (fxProductType === 'SPOT') syncValueDate(setFormData, formData.tradeDate, formData.buyCurrency, e.target.value);
                }}
                className={errors.sellCurrency ? 'error' : ''}
                disabled={isFieldDisabled('sellCurrency')}
              >
                <option value="">Select Currency</option>
                {dimensionData.currencies.map((c: any) => (
                  <option key={c.id} value={c.currencyCode}>
                    {c.currencyCode} - {c.currencyName}
                  </option>
                ))}
              </Select>
              {errors.sellCurrency && <FieldError>{errors.sellCurrency}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="transactionType">Direction*</Label>
              <Select
                id="transactionType"
                value={formData.transactionType}
                onChange={(e) => handleInputChange('transactionType', e.target.value)}
                disabled={isFieldDisabled('transactionType')}
              >
                <option value="BUY">Buy {formData.buyCurrency} / Sell {formData.sellCurrency}</option>
                <option value="SELL">Sell {formData.buyCurrency} / Buy {formData.sellCurrency}</option>
              </Select>
            </FormGroup>
          </FormRow>

          {/* Row 4: Buy Amount, Sell Amount, Rate */}
          <FormRow>
            <FormGroup>
              <Label htmlFor="buyAmount">Buy Amount ({formData.buyCurrency})*</Label>
              <Input
                id="buyAmount"
                type="text"
                value={formData.buyAmount}
                onChange={(e) => handleInputChange('buyAmount', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    setFormData((prev: any) => ({ ...prev, buyAmount: formatAmountWithCommas(parsed) }));
                    calculateCounterAmount(parsed.toString(), formData.sellAmount, formData.rate, 'buy', setFormData, formData.buyCurrency, formData.sellCurrency);
                  }
                }}
                placeholder="Enter amount (e.g., 1M)"
                className={errors.buyAmount ? 'error' : ''}
                disabled={isFieldDisabled('buyAmount')}
              />
              {errors.buyAmount && <FieldError>{errors.buyAmount}</FieldError>}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="sellAmount">Sell Amount ({formData.sellCurrency})*</Label>
              <Input
                id="sellAmount"
                type="text"
                value={formData.sellAmount}
                onChange={(e) => handleInputChange('sellAmount', e.target.value)}
                onBlur={(e) => {
                  const parsed = parseAmountShorthand(e.target.value);
                  if (parsed > 0) {
                    setFormData((prev: any) => ({ ...prev, sellAmount: formatAmountWithCommas(parsed) }));
                    calculateCounterAmount(formData.buyAmount, parsed.toString(), formData.rate, 'sell', setFormData, formData.buyCurrency, formData.sellCurrency);
                  }
                }}
                placeholder="Enter amount (e.g., 1M)"
                className={errors.sellAmount ? 'error' : ''}
                disabled={isFieldDisabled('sellAmount')}
              />
              {errors.sellAmount && <FieldError>{errors.sellAmount}</FieldError>}
            </FormGroup>

            <FormGroup>
              {/* F-FX-015: the label states the market-convention pair and
                  reading, so the operator always types the standard quote
                  (EURUSD 1.1000) regardless of which side they bought. */}
              <Label htmlFor="rate">
                {`Exchange Rate* (${deriveFxPair(formData.buyCurrency, formData.sellCurrency).pair}`}
                {` — ${deriveFxPair(formData.buyCurrency, formData.sellCurrency).quote} per 1 `}
                {`${deriveFxPair(formData.buyCurrency, formData.sellCurrency).base})`}
              </Label>
              <Input
                id="rate"
                type="number"
                step="0.0001"
                value={formData.rate}
                onChange={(e) => handleInputChange('rate', e.target.value)}
                onBlur={(e) => {
                  calculateCounterAmount(formData.buyAmount, formData.sellAmount, e.target.value, 'rate', setFormData, formData.buyCurrency, formData.sellCurrency);
                }}
                placeholder={loadingRate ? 'Loading...' : (marketRate ? `Market: ${marketRate.toFixed(4)}` : 'Enter rate')}
                className={errors.rate ? 'error' : ''}
                disabled={isFieldDisabled('rate')}
              />
              {errors.rate && <FieldError>{errors.rate}</FieldError>}
            </FormGroup>
          </FormRow>
          </FXSection>

          {(fxProductType === 'FORWARD' || fxProductType === 'NDF' || fxProductType === 'FX_SWAP') && (
            <FXSection>
              <FXSectionHeader>
                <div>
                  <FXSectionEyebrow $tone={toneName}>
                    {fxProductType === 'FX_SWAP' ? 'Swap Terms' : fxProductType === 'NDF' ? 'NDF Terms' : 'Forward Terms'}
                  </FXSectionEyebrow>
                  <FXSectionTitle>
                    {fxProductType === 'FX_SWAP' ? 'Far Leg and Swap Points' : fxProductType === 'NDF' ? 'Fixing and Forward Economics' : 'Forward Points and Outright Rate'}
                  </FXSectionTitle>
                  <FXSectionSubline>
                    {fxProductType === 'FX_SWAP'
                      ? 'Define the reverse leg economics and far settlement date.'
                      : fxProductType === 'NDF'
                        ? 'Set the cash-settlement fixing details and derived forward rate.'
                        : 'Set forward points and the resulting outright rate.'}
                  </FXSectionSubline>
                </div>
              </FXSectionHeader>

              {(fxProductType === 'FORWARD' || fxProductType === 'NDF') && (
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="forwardPoints">Forward Points</Label>
                    <Input
                      id="forwardPoints"
                      type="number"
                      step="0.0001"
                      value={formData.forwardPoints}
                      onChange={(e) => {
                        handleInputChange('forwardPoints', e.target.value);
                        // Outright = spot + points/pipFactor. F-FX-015: the pip
                        // factor is 10,000 for 4-decimal pairs but 100 for
                        // JPY/BRL-style 2-decimal quotes — the hardcoded 10,000
                        // understated those outrights by 100x.
                        if (formData.rate && e.target.value) {
                          const spotRate = parseFloat(formData.rate);
                          const points = parseFloat(e.target.value);
                          const { quote } = deriveFxPair(formData.buyCurrency, formData.sellCurrency);
                          const factor = fxPipFactor(quote);
                          const forwardRate = spotRate + (points / factor);
                          setFormData((prev: any) => ({
                            ...prev,
                            forwardRate: forwardRate.toFixed(factor === 100 ? 4 : 6)
                          }));
                        }
                      }}
                      placeholder="e.g., -25.5"
                      disabled={isFieldDisabled('forwardPoints')}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="forwardRate">Forward Rate</Label>
                    <Input
                      id="forwardRate"
                      type="number"
                      step="0.0001"
                      value={formData.forwardRate}
                      onChange={(e) => handleInputChange('forwardRate', e.target.value)}
                      placeholder="Calculated from points"
                      disabled={isFieldDisabled('forwardRate')}
                    />
                  </FormGroup>

                  {fxProductType === 'NDF' && (
                    <FormGroup>
                      <Label htmlFor="fixingDate">Fixing Date*</Label>
                      <DateField
                        id="fixingDate"
                        value={formData.fixingDate ?? ''}
                        className={errors.fixingDate ? 'error' : ''}
                        disabled={isFieldDisabled('fixingDate')}
                        onChange={iso => handleInputChange('fixingDate', iso)}
                      />
                      {errors.fixingDate && <FieldError>{errors.fixingDate}</FieldError>}
                    </FormGroup>
                  )}
                </FormRow>
              )}

              {fxProductType === 'NDF' && (
                <FormRow>
                  <FormGroup>
                    <Label htmlFor="fixingSource">Fixing Source*</Label>
                    <Select
                      id="fixingSource"
                      value={formData.fixingSource}
                      onChange={(e) => handleInputChange('fixingSource', e.target.value)}
                      disabled={isFieldDisabled('fixingSource')}
                    >
                      <option value="WM/Reuters">WM/Reuters</option>
                      <option value="Bloomberg">Bloomberg</option>
                      <option value="Central Bank">Central Bank</option>
                      <option value="EMTA">EMTA</option>
                    </Select>
                  </FormGroup>
                </FormRow>
              )}

              {fxProductType === 'FX_SWAP' && (
                <SwapLegPanel isNear={false}>
                  <SwapLegTitle isNear={false}>
                    <CalendarDays size={16} />
                    Far Leg (Reverse)
                  </SwapLegTitle>
                  <FormRow>
                    <FormGroup>
                      <Label htmlFor="farSettleDate">Far Settle Date*</Label>
                      <DateField
                        id="farSettleDate"
                        value={formData.farSettleDate ?? ''}
                        className={errors.farSettleDate ? 'error' : ''}
                        disabled={isFieldDisabled('farSettleDate')}
                        calendars={fxCalendars(formData.buyCurrency, formData.sellCurrency)}
                        onChange={iso => handleInputChange('farSettleDate', iso)}
                      />
                      {errors.farSettleDate && <FieldError>{errors.farSettleDate}</FieldError>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="farRate">Far Leg Rate*</Label>
                      <Input
                        id="farRate"
                        type="number"
                        step="0.0001"
                        value={formData.farRate}
                        onChange={(e) => handleInputChange('farRate', e.target.value)}
                        className={errors.farRate ? 'error' : ''}
                        disabled={isFieldDisabled('farRate')}
                      />
                      {errors.farRate && <FieldError>{errors.farRate}</FieldError>}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="swapPoints">Swap Points</Label>
                      <Input
                        id="swapPoints"
                        type="number"
                        step="0.0001"
                        value={formData.forwardPoints}
                        onChange={(e) => handleInputChange('forwardPoints', e.target.value)}
                        placeholder="Points difference"
                        disabled={isFieldDisabled('forwardPoints')}
                      />
                    </FormGroup>
                  </FormRow>
                </SwapLegPanel>
              )}
            </FXSection>
          )}

          {!useAllocations && (
            <FXSection>
              <FXSectionHeader>
                <div>
                  <FXSectionEyebrow $tone={toneName}>Portfolio Target</FXSectionEyebrow>
                  <FXSectionTitle>{isRetail ? 'Household and Client Account' : 'Portfolio Assignment'}</FXSectionTitle>
                  <FXSectionSubline>Choose the book that receives the FX trade and settlement cash impact.</FXSectionSubline>
                </div>
              </FXSectionHeader>

              <FormRow>
              {isRetail ? (
                /* Retail: Household + Client Account */
                <>
                  <FormGroup>
                    <Label htmlFor="household">Household</Label>
                    <Select
                      id="household"
                      value={selectedHousehold}
                      onChange={(e) => setSelectedHousehold(e.target.value)}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">All Households</option>
                      {households.map((household: any) => (
                        <option key={household.id} value={household.id.toString()}>
                          {household.householdName || household.name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>
                  <FormGroup>
                    <Label htmlFor="portfolio">Client Account*</Label>
                    <Select
                      id="portfolio"
                      value={formData.portfolio}
                      onChange={(e) => handleInputChange('portfolio', e.target.value)}
                      className={errors.portfolio ? 'error' : ''}
                      disabled={isFieldDisabled('portfolio')}
                    >
                      <option value="">Select Account</option>
                      {(selectedHousehold ? filteredPortfolios : dimensionData.portfolios).map((p: any) => (
                        <option key={p.id} value={p.portfolioName}>
                          {p.portfolioName}
                        </option>
                      ))}
                    </Select>
                    {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                  </FormGroup>
                </>
              ) : (
                /* Institutional: Portfolio */
                <FormGroup>
                  <Label htmlFor="portfolio">Portfolio*</Label>
                  <Select
                    id="portfolio"
                    value={formData.portfolio}
                    onChange={(e) => handleInputChange('portfolio', e.target.value)}
                    className={errors.portfolio ? 'error' : ''}
                    disabled={isFieldDisabled('portfolio')}
                  >
                    <option value="">Select Portfolio</option>
                    {dimensionData.portfolios.map((p: any) => (
                      <option key={p.id} value={p.portfolioName}>
                        {p.portfolioName}
                      </option>
                    ))}
                  </Select>
                  {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
                </FormGroup>
              )}
            </FormRow>
            </FXSection>
          )}

          {/* Rate Summary */}
          {formData.rate && formData.buyAmount && (
            <RateSummary>
              <RateSummaryTitle>
                <ArrowRightLeft size={18} />
                Trade Summary
              </RateSummaryTitle>
              <RateGrid>
                <RateCard>
                  <RateLabel>Buy</RateLabel>
                  <RateValue>
                    {parseAmountShorthand(formData.buyAmount).toLocaleString()} {formData.buyCurrency}
                  </RateValue>
                </RateCard>
                <RateCard>
                  <RateLabel>Sell</RateLabel>
                  <RateValue>
                    {parseAmountShorthand(formData.sellAmount || '0').toLocaleString()} {formData.sellCurrency}
                  </RateValue>
                </RateCard>
                <RateCard>
                  <RateLabel>Rate</RateLabel>
                  <RateValue highlight>{parseFloat(formData.rate).toFixed(4)}</RateValue>
                </RateCard>
                {fxProductType === 'FX_SWAP' && formData.farRate && (
                  <RateCard>
                    <RateLabel>Swap Points</RateLabel>
                    <RateValue>
                      {((parseFloat(formData.farRate) - parseFloat(formData.rate))
                        * fxPipFactor(deriveFxPair(formData.buyCurrency, formData.sellCurrency).quote)).toFixed(1)}
                    </RateValue>
                  </RateCard>
                )}
                <RateCard>
                  <RateLabel>Product Type</RateLabel>
                  <RateValue>{fxProductType}</RateValue>
                </RateCard>
                <RateCard>
                  <RateLabel>Value Date</RateLabel>
                  <RateValue style={{ fontSize: '14px' }}>{formData.settleDate}</RateValue>
                </RateCard>
              </RateGrid>
            </RateSummary>
          )}
        </FXWorkspace>
        );
      }}
    </BaseTicketScreen>
  );
};

export default FXTicketScreen;
